"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.analyzeRestaurantClosure = analyzeRestaurantClosure;
exports.analyzeReservationCutoffs = analyzeReservationCutoffs;
exports.analyzeTableOverrides = analyzeTableOverrides;
exports.analyzeBlockingReservations = analyzeBlockingReservations;
exports.analyzeAvailability = analyzeAvailability;
const isArray_1 = __importDefault(require("lodash/isArray"));
const hasIn_1 = __importDefault(require("lodash/hasIn"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const luxon_1 = require("luxon");
const TDR_1 = require("../types/TDR");
const invoice_1 = require("./invoice");
const reservation_1 = require("./reservation");
const bookingType_1 = require("./bookingType");
const horizon_1 = require("./horizon");
const time_1 = require("./time");
function analyzeRestaurantClosure(date, restaurant, policies) {
    var _a, _b, _c, _d;
    if ((_b = (_a = restaurant === null || restaurant === void 0 ? void 0 : restaurant.closed) === null || _a === void 0 ? void 0 : _a.weekdays) === null || _b === void 0 ? void 0 : _b.includes(date.weekdayLong)) {
        return { closed: true, reason: 'weekday', weekday: date.weekdayLong };
    }
    const dateStr = date.toFormat('MMMM dd, yyyy');
    const closedDateFound = (_d = (_c = restaurant === null || restaurant === void 0 ? void 0 : restaurant.closed) === null || _c === void 0 ? void 0 : _c.dates) === null || _d === void 0 ? void 0 : _d.find(d => d.date === dateStr);
    if (closedDateFound) {
        return {
            closed: true,
            reason: 'date',
            dateDescription: (0, reservation_1.getClosedDateDescription)(date, restaurant) || closedDateFound.description
        };
    }
    if ((0, horizon_1.isDateBeyondHorizon)(date, { policies, restaurant })) {
        const { source: horizonSource } = (0, horizon_1.resolveHorizonDate)({ policies, restaurant });
        const policy = (0, horizon_1.findBookingHorizonPolicy)(policies);
        return {
            closed: true,
            reason: 'beyondBookableDays',
            bookableDaysInAdvance: restaurant.bookableDaysInAdvance,
            horizonSource,
            horizonPolicyId: policy === null || policy === void 0 ? void 0 : policy.id
        };
    }
    return { closed: false };
}
function analyzeReservationCutoffs(reservation, restaurant) {
    var _a, _b;
    const configs = (restaurant === null || restaurant === void 0 ? void 0 : restaurant.reservationCutoffs) || [];
    let matchedIndex = -1;
    for (let i = 0; i < configs.length; i++) {
        if ((0, hasIn_1.default)(configs[i], 'create') &&
            (0, invoice_1.policyConditionApplies)(configs[i].condition, { reservation })) {
            matchedIndex = i;
            break;
        }
    }
    const matched = matchedIndex === -1 ? TDR_1.TDR.DEFAULT_RESERVATION_CUTOFFS : configs[matchedIndex];
    const cutoffs = (0, reservation_1.computeCutoffs)(reservation, restaurant);
    const now = luxon_1.DateTime.local();
    const cutoffTime = (_a = cutoffs === null || cutoffs === void 0 ? void 0 : cutoffs.create) !== null && _a !== void 0 ? _a : null;
    return {
        now: now.toISO(),
        configIndex: matchedIndex,
        isDefault: matchedIndex === -1,
        create: {
            timeBefore: (_b = matched.create) !== null && _b !== void 0 ? _b : null,
            cutoffTime: cutoffTime ? cutoffTime.toISO() : null,
            cutOff: !!(cutoffTime && cutoffTime < now)
        }
    };
}
function analyzeTableOverrides(date, restaurant, table, groupSize) {
    var _a;
    const applied = [];
    const skipped = [];
    for (const override of (restaurant === null || restaurant === void 0 ? void 0 : restaurant.overrides) || []) {
        // One computation, shared with the evaluator. This module exists to explain what the real
        // evaluator does, so it must never re-derive matching — a second copy could drift and then
        // report a reason that contradicts the decision actually taken.
        const { targetMatch, dateMatch, guestApplies, hasGuestCondition } = (0, reservation_1.matchOverride)(override, table, date, { groupSize });
        if (!targetMatch) {
            skipped.push({ id: override.id, name: override.name, reason: 'noTargetMatch' });
            continue;
        }
        if (!dateMatch) {
            skipped.push({ id: override.id, name: override.name, reason: 'noDateMatch' });
            continue;
        }
        if (!guestApplies) {
            skipped.push({ id: override.id, name: override.name, reason: 'guestOutOfRange' });
            continue;
        }
        const guestCondition = !hasGuestCondition
            ? 'none'
            : groupSize === undefined
                ? 'unconditional'
                : 'matched';
        applied.push({
            id: override.id,
            name: override.name,
            targetMatch,
            dateMatch,
            guestCondition,
            affectedTimeStrs: Object.keys(((_a = override.values) === null || _a === void 0 ? void 0 : _a.defaultTimeSlots) || {})
        });
    }
    return { applied, skipped };
}
function analyzeBlockingReservations(date, timeStr, restaurant, table, dbTsDoc, groupSize) {
    var _a, _b, _c, _d;
    const slotTime = date.set((0, time_1.parseTimeNoAPM)(timeStr));
    const baseSlots = (0, reservation_1.computeBaseTimeSlots)(date, restaurant, table, { groupSize });
    const slot = baseSlots[timeStr];
    const minutesBefore = (_a = slot === null || slot === void 0 ? void 0 : slot.resDuration) !== null && _a !== void 0 ? _a : time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesBefore;
    const defaultMinAfter = time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter;
    const reservations = (0, mapValues_1.default)(dbTsDoc.reservations || {}, idOrArr => (0, isArray_1.default)(idOrArr) ? idOrArr : [idOrArr]);
    const blockingDurations = {};
    for (const [t, ids] of Object.entries(reservations)) {
        const stored = ids
            .map(id => { var _a; return (_a = dbTsDoc.resDurations) === null || _a === void 0 ? void 0 : _a[id]; })
            .find(d => d !== undefined);
        blockingDurations[t] = (_c = stored !== null && stored !== void 0 ? stored : (_b = baseSlots[t]) === null || _b === void 0 ? void 0 : _b.resDuration) !== null && _c !== void 0 ? _c : defaultMinAfter;
    }
    const out = [];
    for (const [resTimeStr, ids] of Object.entries(reservations)) {
        const resTime = date.set((0, time_1.parseTimeNoAPM)(resTimeStr));
        const timeDiff = slotTime.diff(resTime, 'minutes').minutes;
        const exact = timeDiff === 0;
        const minutesAfter = (_d = blockingDurations[resTimeStr]) !== null && _d !== void 0 ? _d : defaultMinAfter;
        const withinAdvanceMargin = timeDiff < 0 && Math.abs(timeDiff) < minutesBefore;
        const withinReservationMargin = timeDiff > 0 && timeDiff < minutesAfter;
        if (!exact && !withinAdvanceMargin && !withinReservationMargin) {
            continue;
        }
        let kind;
        if (exact) {
            kind = 'exactSlot';
        }
        else if (withinAdvanceMargin) {
            kind = 'before';
        }
        else {
            kind = 'after';
        }
        for (const id of ids) {
            out.push({
                reservationId: id,
                timeStr: resTimeStr,
                blockingDuration: minutesAfter,
                kind,
                timeDiffMinutes: Math.abs(timeDiff)
            });
        }
    }
    return out;
}
function analyzeAvailability(input) {
    var _a;
    const { restaurant, tables, dbTsDocs, dateStr, timeStr, guests } = input;
    const zone = restaurant.timezone || 'UTC';
    const date = luxon_1.DateTime.fromFormat(dateStr, time_1.DATESTR_FMT, { zone });
    const slotTime = date.set((0, time_1.parseTimeNoAPM)(timeStr));
    const restaurantClosure = analyzeRestaurantClosure(date, restaurant, input.policies);
    const dbDocByTableId = {};
    for (const doc of dbTsDocs) {
        if (doc.tableId) {
            dbDocByTableId[doc.tableId] = doc;
        }
    }
    const firstTable = tables[0];
    const sharedCutoffs = firstTable
        ? analyzeReservationCutoffs((0, reservation_1.approximateReservation)(slotTime, firstTable, guests), restaurant)
        : analyzeReservationCutoffs({
            time: { seconds: slotTime.toSeconds(), nanoseconds: 0 },
            timezone: zone,
            guests
        }, restaurant);
    const tableReports = tables.map(table => {
        var _a;
        const guestRangeMatches = guests >= table.minGuests && guests <= table.maxGuests;
        const overrides = analyzeTableOverrides(date, restaurant, table, guests);
        const effectiveTable = (0, reservation_1.computeEffectiveTable)(date, restaurant, table, { groupSize: guests });
        const dbDoc = dbDocByTableId[table.id] || { dateStr };
        const tsDoc = (0, reservation_1.unpackTimeSlots)(restaurant, table, dbDoc, { groupSize: guests, ignoreCutoffs: true });
        const slot = (_a = tsDoc.timeSlots) === null || _a === void 0 ? void 0 : _a[timeStr];
        const blockers = analyzeBlockingReservations(date, timeStr, restaurant, table, dbDoc, guests);
        const reservation = (0, reservation_1.approximateReservation)(slotTime, table, guests);
        const cutoffs = analyzeReservationCutoffs(reservation, restaurant);
        const cutoffApplies = cutoffs.create.cutOff;
        const existsAfterOverrides = !!slot;
        const finalAvailable = !restaurantClosure.closed &&
            guestRangeMatches &&
            existsAfterOverrides &&
            (slot === null || slot === void 0 ? void 0 : slot.available) !== false &&
            blockers.length === 0 &&
            !cutoffApplies;
        const reasons = [];
        if (restaurantClosure.closed) {
            reasons.push(`restaurant closed (${restaurantClosure.reason})`);
        }
        if (!guestRangeMatches) {
            reasons.push(`guests ${guests} out of range [${table.minGuests}, ${table.maxGuests}]`);
        }
        if (!existsAfterOverrides) {
            reasons.push(`time ${timeStr} not in default/overridden slots`);
        }
        else if ((slot === null || slot === void 0 ? void 0 : slot.available) === false) {
            reasons.push('slot marked unavailable in schedule');
        }
        if (blockers.length) {
            reasons.push(`${blockers.length} blocking reservation(s)`);
        }
        if (cutoffApplies) {
            reasons.push('past create cutoff');
        }
        const summary = finalAvailable ? 'Available' : `Not available: ${reasons.join('; ')}`;
        return {
            tableId: table.id,
            tableName: table.name || table.slug,
            tableSlug: table.slug,
            minGuests: table.minGuests,
            maxGuests: table.maxGuests,
            bookingType: (0, bookingType_1.getBookingType)(effectiveTable),
            guestRangeMatches,
            appliedOverrides: overrides.applied,
            skippedOverrides: overrides.skipped,
            requestedSlot: { existsAfterOverrides, available: slot === null || slot === void 0 ? void 0 : slot.available },
            blockingReservations: blockers,
            cutoffApplies,
            finalAvailable,
            summary
        };
    });
    const anyTableAvailable = tableReports.some(t => t.finalAvailable);
    let overallReason;
    if (anyTableAvailable) {
        overallReason = 'At least one table is available';
    }
    else if (restaurantClosure.closed) {
        overallReason = `Restaurant closed (${restaurantClosure.reason})`;
    }
    else if (sharedCutoffs.create.cutOff && tableReports.length && tableReports.every(t => t.cutoffApplies)) {
        overallReason = 'Past create cutoff for this slot';
    }
    else {
        overallReason = 'No table satisfies guest count, time, and reservation constraints';
    }
    return {
        input: {
            restaurantId: (_a = restaurant.id) !== null && _a !== void 0 ? _a : '',
            dateStr,
            timeStr,
            guests
        },
        timezone: zone,
        summary: { anyTableAvailable, overallReason },
        restaurantClosure,
        cutoffs: sharedCutoffs,
        tables: tableReports
    };
}
//# sourceMappingURL=debugAvailability.js.map